import {
  Box,
  Button,
  chakra,
  Checkbox,
  Flex,
  Heading,
  IconButton,
  Menu,
  MenuButton, MenuItem,
  MenuList, HStack, SimpleGrid, Stack, Tag, Text, Tooltip, Wrap, WrapItem,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { ArrowCounterClockwise, CheckCircle, Checks, DotsThreeVertical, Eye, Flame, Lightning, PencilSimple, Rows, SquaresFour, Trash, Truck } from "@phosphor-icons/react";
import TaskCard from "./components/TaskCard";
import TaskFilterBar from "./components/TaskFilterBar";
import SendReminderModal from "./components/SendReminderModal";
import { DeleteDialog } from "../DeleteDialog";
import { ConfirmationDialog } from "../../../../components/ConfirmationDialog";
import { DataTable } from "../../../../components/DataTable";
import { useDispatch } from "react-redux";
import { useQueryClient } from "react-query";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import {
  dimensionsKeys,
  getSortDirection,
  QUARTERS,
  QUESTIONNAIRE_STATUSES,
  showErrorToast,
  showSuccessToast,
  SORT_DIRECTIONS
} from "../../../../utils";
import {
  organizationQuestionnaireKeys,
  useDeleteQuestionnaire,
  useFinalizeQuestionnaire,
  useGetAllDataEntryPlanningQuestionnaires,
  useGetOrganisationQuestionnaires,
  useGetOrganizationStructure,
  useUnfinaliseOrgQuestionnaire,
} from "../../../../hooks/query";
import { CATEGORIES_STEPS } from "../../../OrganizationQuestionnaire/components/Categories/const";
import { useTranslation } from "react-i18next";
import { WithAuthorization, useAuth } from "../../../../context";
import { ReviewQuestionnaireModal } from "../ReviewQuestionnaireModal";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../../config";
import { MultiSelectFilter } from "../../../../components/DataTable/components";
import { compareLabels } from "../../../../utils/userSort";

export const getRequestedQuestionnaires = (questionnaireList) => {
  const listRaw = Object.keys(questionnaireList).filter(a => a.endsWith("ShouldBeFilled") && questionnaireList[a]);
  let questionnaireString = "";
  listRaw.map((q, index) => {
    const editedTitle = q.substring(0, q.indexOf("ShouldBeFilled"));
    let questionnaire = "";
    switch (editedTitle) {
      case "directEmissions":
        questionnaire = "Scope 1";
        break;
      case "indirectEmissions":
        questionnaire = "Scope 2";
        break;
      default:
        questionnaire = `Category ${CATEGORIES_STEPS.indexOf(editedTitle) + 1}`;
        break;
    }

    return questionnaireString = questionnaireString + questionnaire + `${index !== listRaw.length - 1 ? ", " : ""}`;
  });
  return questionnaireString;
};

const SCOPE_COLORS = {
  scope1: { light: "#E8FAF0", text: "#0B5E30", icon: Flame, label: "Scope 1" },
  scope2: { light: "#E8F4FD", text: "#1A5276", icon: Lightning, label: "Scope 2" },
  scope3: { light: "#F3E8FE", text: "#5B1D99", icon: Truck, label: "Scope 3" },
};

const parseScopeTagsFromConfig = (configurations) => {
  if (!configurations) return [];
  const tags = [];
  if (configurations.directEmissionsShouldBeFilled) tags.push({ ...SCOPE_COLORS.scope1 });
  if (configurations.indirectEmissionsShouldBeFilled) tags.push({ ...SCOPE_COLORS.scope2 });
  const cats = [];
  const catFields = [
    ["purchasedGoodsAndServicesShouldBeFilled", "1"],
    ["capitalGoodsShouldBeFilled", "2"],
    ["fuelAndEnergyRelatedActivitiesShouldBeFilled", "3"],
    ["upstreamTransportationAndDistributionShouldBeFilled", "4"],
    ["wasteGeneratedInOperationsShouldBeFilled", "5"],
    ["businessTravelShouldBeFilled", "6"],
    ["employeeCommutingShouldBeFilled", "7"],
    ["downstreamTransportationAndDistributionShouldBeFilled", "9"],
    ["useOfSoldProductsShouldBeFilled", "11"],
    ["treatmentOfSoldProductsShouldBeFilled", "12"],
    ["franchisesShouldBeFilled", "14"],
    ["investmentsShouldBeFilled", "15"],
  ];
  catFields.forEach(([field, label]) => { if (configurations[field]) cats.push(label); });
  if (cats.length > 0) tags.push({ ...SCOPE_COLORS.scope3, categories: cats });
  return tags;
};

function OrganizationPlanning() {
  const { t } = useTranslation("translation", {
    keyPrefix: "dataEntryPlanning"
  });

  const { userPermissions } = useAuth();
  const isAdmin = userPermissions?.includes("ORG_ADMIN");

  const toast = useToast();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();

  const [selectedRowId, setSelectedRowId] = useState(null);
  const [selectedQuestionnaireId, setSelectedQuestionnaireId] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkAction, setBulkAction] = useState(null); // "delete" | "finalise" | "unfinalise"
  const {
    isOpen: isBulkDialogOpen,
    onOpen: onBulkDialogOpen,
    onClose: onBulkDialogClose,
  } = useDisclosure();

  const {
    isOpen: isReviewQuestionnaireOpen,
    onOpen: onReviewQuestionnaireOpen,
    onClose: onReviewQuestionnaireClose
  } = useDisclosure();

  // "Send reminder". `reminderTask` is the task open in the compose modal. Each card's "Reminder sent …"
  // line is the server's `lastReminder` (the send refetches the list); `justRemindedIds` only decides
  // which of those lines get the lime "just now" highlight, so it is the one piece of client state here.
  const [reminderTask, setReminderTask] = useState(null);
  const { isOpen: isReminderOpen, onOpen: onReminderOpen, onClose: onReminderClose } = useDisclosure();
  const [justRemindedIds, setJustRemindedIds] = useState(() => new Set());

  const openSendReminder = (task) => {
    setReminderTask(task);
    onReminderOpen();
  };

  const handleReminderSent = ({ task, reminder }) => {
    setJustRemindedIds((prev) => new Set(prev).add(task.id));
    const ccCount = reminder?.ccCount ?? 0;
    toast({
      title: "Reminder sent",
      description: `Sent to ${reminder?.recipientEmail || task.assigneeName}${ccCount > 0 ? ` with ${ccCount} ${ccCount === 1 ? "person" : "people"} in copy` : ""}`,
      status: "success",
      duration: 5000,
      isClosable: true,
    });
  };

  const [viewType, setViewType] = useState(() => {
    return sessionStorage.getItem("dataEntryPlanningViewType") || "grid";
  });

  const handleSetViewType = (type) => {
    setViewType(type);
    sessionStorage.setItem("dataEntryPlanningViewType", type);
  };
  const [searchTerm, setSearchTerm] = useState("");
  const [localFilters, setLocalFilters] = useState({ assignee: [], scope: [], dimension: [], year: [] });
  const [tasksList, setTasksList] = useState([]);

  const { data: organizationStructure } = useGetOrganizationStructure();

  const [dataTableState, setDataTableState] = useState({
    queryPageIndex: 0,
    queryPageSize: 10,
    queryPageSortBy: [`id,${SORT_DIRECTIONS.DESC}`],
    totalElements: 0,
    totalPages: 0,
    filterObj: {}
  });

  const { totalPages, filterObj, queryPageIndex, queryPageSize, queryPageSortBy, totalElements } = dataTableState;

  const manageDataTableState = (newValObject) => {
    const calcState = (prevState) => {
      let changedAttributes = {};

      Object.keys(newValObject).forEach(dataTableAttribute => {
        const value = newValObject[dataTableAttribute];

        if (dataTableAttribute === "filterObj") {
          // We have to save not only the new value sent from the data table
          // but also the previously applied filters, which we get from prevState
          changedAttributes[dataTableAttribute] = {
            ...prevState[dataTableAttribute],
            [value.attribute]: value.value
          };
        } else if (dataTableAttribute === "queryPageSortBy") {
          let stringValue = value[0];
          let accessorName = stringValue.split(",")[0];
          const sortDirection = stringValue.split(",")[1];
          const updatedArray = [...prevState[dataTableAttribute]];

          if (accessorName === "assigneeName") {
            // Back-end requires the usage of 'user.email' instead of 'assigneeName' for sorting
            stringValue = `user.email,${sortDirection}`;
            accessorName = "user.email";
          } else if (accessorName === "year") {
            // Back-end requires the usage of 'period.year' instead of 'year' for sorting
            stringValue = `period.year,${sortDirection}`;
            accessorName = "period.year";
          } else if (accessorName === "quarter") {
            // Back-end requires the usage of 'period.quarter' instead of 'quarter' for sorting
            stringValue = `period.quarter,${sortDirection}`;
            accessorName = "period.quarter";
          }

          // Removing initial sorting by id, if still available
          const indexOfInitial = updatedArray.findIndex(el => {
            return el.split(",")[0] === "id";
          });
          if (indexOfInitial > -1) {
            updatedArray.splice(indexOfInitial, 1);
          }

          const indexOfExisting = updatedArray.findIndex(el => {
            return el.split(",")[0] === accessorName;
          });
          if (indexOfExisting > -1) {
            // Column which we try to sort is already in the sort list, so we just manipulate its record
            if (sortDirection === SORT_DIRECTIONS.NONE) {
              updatedArray.splice(indexOfExisting, 1);
            } else {
              updatedArray[indexOfExisting] = stringValue;
            }
          } else {
            // Column which we try to sort does not exist in the sort list, so we add it
            updatedArray.push(stringValue);
          }

          changedAttributes[dataTableAttribute] = updatedArray;
        } else {
          // All other values have primitive types, so we apply them directly
          // without checking their previous states
          changedAttributes[dataTableAttribute] = value;
        }
      });

      return changedAttributes;
    };

    setDataTableState((prevState) => {
      return {
        ...prevState,
        ...calcState(prevState)
      };
    });
  };

  const handleErrors = (error) => {
    let errorMsg = error.response?.status === 409 &&
    error.response?.data?.parameters?.resourceName === "OrganizationQuestionnaire" ?
      t("page.duplicatedAssignee", { typeOfQuestionnaire: "" }) : error.response?.status === 400 &&
      error.response?.data?.errorCode === "object-argument-not-valid" ? error.response?.data?.parameters?.message :
        error.message;

    dispatch(hideAppSpinner());
    showErrorToast(toast, errorMsg);
  };


  const { mutate: deleteTask } = useDeleteQuestionnaire({
    onSuccess: () => {
      onConfirmDialogClose();
      setSelectedRowId(null);
      showSuccessToast(toast, t("messages.taskDeleted"));
      queryClient.invalidateQueries(organizationQuestionnaireKeys.allDataEntryPlanningQuestionnaires());
      refetchTasks();
      dispatch(hideAppSpinner());
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutate: unfinaliseQuestionnaire } = useUnfinaliseOrgQuestionnaire({
    onMutate: () => {
      dispatch(showAppSpinner());
    },
    onSuccess: () => {
      showSuccessToast(toast, t("messages.unfinaliseSuccess"));
      queryClient.invalidateQueries(organizationQuestionnaireKeys.allDataEntryPlanningQuestionnaires());
      refetchTasks();
      dispatch(hideAppSpinner());
    },
    onError: err => handleErrors(err),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  // Raw mutateAsync for bulk operations — we manage toasts + invalidation ourselves
  // so parallel failures don't each fire their own toast.
  const { mutateAsync: deleteTaskAsync } = useDeleteQuestionnaire();
  const { mutateAsync: unfinaliseTaskAsync } = useUnfinaliseOrgQuestionnaire();
  const { mutateAsync: finaliseTaskAsync } = useFinalizeQuestionnaire({
    onMutate: () => {},
    onSuccess: () => {},
    onError: () => {},
    onSettled: () => {},
  });

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const toggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => {
      if (prev) setSelectedIds(new Set());
      return !prev;
    });
  }, []);

  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Kept in sync in useEffect below so the column header (built inside a
  // useMemo above filteredTasksList) can read the current list lazily.
  const filteredTasksListRef = useRef([]);
  // Lets the bulk-action callback call refetchTasks without depending on its
  // hoisted declaration order.
  const refetchTasksRef = useRef(null);

  const toggleSelectAll = useCallback(() => {
    const list = filteredTasksListRef.current;
    setSelectedIds((prev) => {
      const allSelected = list.length > 0 && list.every((task) => prev.has(task.id));
      return allSelected ? new Set() : new Set(list.map((task) => task.id));
    });
  }, []);

  const runBulk = useCallback(async (action, ids) => {
    if (!ids || ids.length === 0) return;
    dispatch(showAppSpinner());
    const mutate =
      action === "delete" ? deleteTaskAsync :
        action === "finalise" ? finaliseTaskAsync :
          unfinaliseTaskAsync;
    const results = await Promise.allSettled(ids.map((id) => mutate(id)));
    const succeeded = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - succeeded;
    const actionLabel = action === "delete" ? "deleted" : action === "finalise" ? "finalised" : "unfinalised";
    if (succeeded > 0 && failed === 0) {
      showSuccessToast(toast, `${succeeded} task${succeeded !== 1 ? "s" : ""} ${actionLabel}`);
    } else if (succeeded > 0 && failed > 0) {
      showErrorToast(toast, `${succeeded} ${actionLabel}, ${failed} failed`);
    } else {
      showErrorToast(toast, `Failed to ${action === "delete" ? "delete" : action} ${failed} task${failed !== 1 ? "s" : ""}`);
    }
    // Log failure reasons for debugging
    results.forEach((r, i) => {
      if (r.status === "rejected") {
        // eslint-disable-next-line no-console
        console.warn(`Bulk ${action} failed for task ${ids[i]}:`, r.reason);
      }
    });
    queryClient.invalidateQueries(organizationQuestionnaireKeys.allDataEntryPlanningQuestionnaires());
    refetchTasksRef.current?.();
    clearSelection();
    dispatch(hideAppSpinner());
  }, [dispatch, deleteTaskAsync, finaliseTaskAsync, unfinaliseTaskAsync, toast, queryClient, clearSelection]);

  const openBulkDialog = useCallback((action) => {
    setBulkAction(action);
    onBulkDialogOpen();
  }, [onBulkDialogOpen]);

  const confirmBulkDialog = useCallback(() => {
    const ids = [...selectedIds];
    onBulkDialogClose();
    runBulk(bulkAction, ids);
    setBulkAction(null);
  }, [bulkAction, selectedIds, onBulkDialogClose, runBulk]);

  function onCreateNew() {
    navigate(routes.dataCollectionNewTask);
  }


  const handleEdit = useCallback((task) => {
    navigate(`${routes.dataCollectionNewTask}?id=${task.id}`);
  }, [navigate]);

  const handleDelete = useCallback((rowId) => {
    setSelectedRowId(rowId);
    onConfirmDialogOpen();
  }, [onConfirmDialogOpen]);

  const handleViewQuestionnaire = useCallback((recordId) => {
    navigate(`${routes.organisationQuestionnaire}?id=${recordId}&view=true`);
  }, [navigate]);

  const handleEditQuestionnaire = useCallback((questionnaireId) => {
    navigate(`${routes.organisationQuestionnaire}?id=${questionnaireId}`);
  }, [navigate]);

  const handleUnfinaliseQuestionnaire = useCallback((questionnaireId) => {
    unfinaliseQuestionnaire(questionnaireId);
  }, [unfinaliseQuestionnaire]);

  const columns = useMemo(() => {
      const baseColumns = [
        {
          header: "Description",
          accessorKey: "description",
          size: 120,
          isSortable: false
        },
        ...(isAdmin ? [{
          header: "Assignee",
          accessorKey: "assigneeName",
          size: 140,
          sortDirection: getSortDirection(queryPageSortBy, "user.email")
        }] : []),
        {
          header: "Year",
          accessorKey: "year",
          size: 80,
          sortDirection: getSortDirection(queryPageSortBy, "period.year")
        },
        {
          header: "Quarter",
          accessorKey: "quarter",
          size: 100,
          sortDirection: getSortDirection(queryPageSortBy, "period.quarter"),
          filter: {
            params: {
              options: Object.keys(QUARTERS).map(key => {
                return {
                  label: key,
                  value: key
                };
              }),
              filterObj,
              setFilterObj: (newFilterObj) => manageDataTableState({
                  filterObj: newFilterObj,
                  queryPageIndex: 0
                }
              ),
              isClearable: false
            },
            component: MultiSelectFilter
          }
        },
        {
          header: "Status",
          accessorKey: "status",
          size: 120,
          sortDirection: getSortDirection(queryPageSortBy, "status"),
          filter: {
            params: {
              options: [
                {
                  label: t(`status.${QUESTIONNAIRE_STATUSES.FINALIZED}`),
                  value: QUESTIONNAIRE_STATUSES.FINALIZED
                },
                {
                  label: t(`status.${QUESTIONNAIRE_STATUSES.IN_PROGRESS}`),
                  value: QUESTIONNAIRE_STATUSES.IN_PROGRESS
                },
                {
                  label: t(`status.${QUESTIONNAIRE_STATUSES.NEW}`),
                  value: QUESTIONNAIRE_STATUSES.NEW
                }
              ],
              filterObj,
              setFilterObj: (newFilterObj) => manageDataTableState({
                  filterObj: newFilterObj,
                  queryPageIndex: 0
                }
              ),
              isClearable: false
            },
            component: MultiSelectFilter
          }
        },
        {
          header: "Scope",
          accessorKey: "questionnaires",
          size: 300,
          enableColumnFilter: false,
          isSortable: false,
          skipNumericCheck: true,
          cell: (row) => {
            const tags = parseScopeTagsFromConfig(row.row.original.configurations);
            return (
              <Wrap spacing={1} minW={0}>
                {tags.map((scope, idx) => {
                  // Replace the regular space in "Scope N" with a non-breaking
                  // space so the label can never split across two lines, no
                  // matter how narrow the table cell is.
                  const safeLabel = scope.label.replace(/ /g, " ");
                  return (
                    <WrapItem key={idx}>
                      <Flex align="center" gap={0}>
                        <Box
                          display="inline-flex"
                          alignItems="center"
                          gap="4px"
                          bg={scope.light}
                          color={scope.text}
                          fontSize="11px"
                          fontWeight="600"
                          px="8px"
                          h="20px"
                          borderRadius="full"
                          borderRightRadius={scope.categories ? 0 : "full"}
                          flexShrink={0}
                          width="max-content"
                          minWidth="max-content"
                          whiteSpace="nowrap"
                        >
                          <scope.icon size={12} weight="fill" style={{ flexShrink: 0 }} />
                          {safeLabel}
                        </Box>
                        {scope.categories && (
                          <Box
                            display="inline-flex"
                            alignItems="center"
                            bg={scope.light}
                            color={scope.text}
                            fontSize="11px"
                            fontWeight="400"
                            px="8px"
                            minH="20px"
                            borderRadius="full"
                            borderLeftRadius={0}
                            opacity={0.8}
                            minW={0}
                            sx={{
                              whiteSpace: "normal",
                              wordBreak: "normal",
                              overflowWrap: "normal",
                            }}
                          >
                            · Cat {scope.categories.join(", ")}
                          </Box>
                        )}
                      </Flex>
                    </WrapItem>
                  );
                })}
              </Wrap>
            );
          },
        },
        {
          header: <div style={{ textAlign: "left" }}><b>Actions</b></div>,
          accessorKey: "action",
          size: 60,
          enableColumnFilter: false,
          isSortable: false,
          skipNumericCheck: true,
          cell: row => (
            <Menu isLazy>
              <Tooltip label={t("page.actionsTooltip")}>
                <MenuButton
                  as={IconButton}
                  aria-label="Actions"
                  icon={<DotsThreeVertical size={20} weight="bold" />}
                  variant="ghost"
                  size="sm"
                  borderRadius="full"
                />
              </Tooltip>
              <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1} minW="180px">
                <MenuItem icon={<Eye size={16} />} onClick={() => handleViewQuestionnaire(row.row.original.id)} fontSize="sm" color="gray.700">
                  {t("page.viewEmissionsData")}
                </MenuItem>
                <MenuItem icon={<PencilSimple size={16} />} onClick={() => handleEditQuestionnaire(row.row.original.id)} isDisabled={row.row.original.originalStatus === QUESTIONNAIRE_STATUSES.FINALIZED} fontSize="sm" color="gray.700">
                  {t("page.editEmissionsData")}
                </MenuItem>
                {isAdmin && (
                  <MenuItem icon={<PencilSimple size={16} />} onClick={() => handleEdit(row.row.original)} isDisabled={!row.row.original.updatable} fontSize="sm" color="gray.700">
                    {t("page.editTask")}
                  </MenuItem>
                )}
                {isAdmin && (
                  <MenuItem icon={<ArrowCounterClockwise size={16} />} onClick={() => handleUnfinaliseQuestionnaire(row.row.original.id)} isDisabled={row.row.original.originalStatus !== QUESTIONNAIRE_STATUSES.FINALIZED} fontSize="sm" color="gray.700">
                    {t("page.unfinaliseGHGQuestionnaire")}
                  </MenuItem>
                )}
                {isAdmin && (
                  <Tooltip label={row.row.original.originalStatus === QUESTIONNAIRE_STATUSES.FINALIZED ? "Finalised tasks cannot be deleted — unfinalise first" : ""} placement="left">
                    <MenuItem icon={<Trash size={16} color="#E53E3E" />} onClick={() => handleDelete(row.row.original.id)} isDisabled={row.row.original.originalStatus === QUESTIONNAIRE_STATUSES.FINALIZED} fontSize="sm" color="red.500">
                      {t("page.deleteTask")}
                    </MenuItem>
                  </Tooltip>
                )}
              </MenuList>
            </Menu>
          )
        }
      ];

      if (!isAdmin) return baseColumns;

      const selectionColumn = {
        header: () => {
          const list = filteredTasksListRef.current;
          const allChecked = list.length > 0 && list.every((task) => selectedIds.has(task.id));
          const someChecked = selectedIds.size > 0 && !allChecked;
          return (
            <Checkbox
              isChecked={allChecked}
              isIndeterminate={someChecked}
              onChange={toggleSelectAll}
              colorScheme="limeShade"
              size="sm"
              sx={{
                ".chakra-checkbox__control": {
                  borderRadius: "sm",
                  borderColor: "gray.400",
                  bg: "white",
                },
                ".chakra-checkbox__control[data-checked]": {
                  bg: "lime",
                  borderColor: "lime",
                  color: "pureBlack",
                },
              }}
            />
          );
        },
        accessorKey: "__selection",
        size: 40,
        enableColumnFilter: false,
        isSortable: false,
        skipNumericCheck: true,
        cell: (row) => {
          const id = row.row.original.id;
          return (
            <Checkbox
              isChecked={selectedIds.has(id)}
              onChange={() => toggleSelected(id)}
              onClick={(e) => e.stopPropagation()}
              colorScheme="limeShade"
              size="sm"
              sx={{
                ".chakra-checkbox__control": {
                  borderRadius: "sm",
                  borderColor: "gray.400",
                  bg: "white",
                },
                ".chakra-checkbox__control[data-checked]": {
                  bg: "lime",
                  borderColor: "lime",
                  color: "pureBlack",
                },
              }}
            />
          );
        },
      };

      return [selectionColumn, ...baseColumns];
    }, [t, handleEdit, handleDelete, handleViewQuestionnaire, handleEditQuestionnaire, handleUnfinaliseQuestionnaire,
      queryPageSortBy, filterObj, isAdmin, selectedIds, toggleSelectAll, toggleSelected]
  );

  function onDelete() {
    dispatch(showAppSpinner());
    deleteTask(selectedRowId);
  }

  const constructQueryParams = () => {
    let multipleValuesParams = "";
    queryPageSortBy.forEach(sortStr => {
      multipleValuesParams += `&sort=${sortStr}`;
    });
    return `page=0&size=9999${multipleValuesParams}`;
  };

  const handleTasksSuccess = useCallback((data) => {
    const pageContent = data?.content ?? [];
    const tasks = pageContent.map((task) => {
      const dimensions = [];
      if (organizationStructure?.dimensions) {
        const dimItemKeyMap = {
          dimensionOne: "dimensionOneItem",
          dimensionTwo: "dimensionTwoItem",
          dimensionThree: "dimensionThreeItem",
          dimensionFour: "dimensionFourItem",
          dimensionFive: "dimensionFiveItem",
        };
        dimensionsKeys.forEach((dimKey) => {
          const dim = organizationStructure.dimensions[dimKey];
          if (dim) {
            const itemId = task?.dimensionItems?.[dimItemKeyMap[dimKey]];
            const item = itemId ? dim.items?.find((i) => i.id === itemId) : null;
            dimensions.push({ name: dim.name, value: item ? item.name : "All" });
          }
        });
      }
      return {
        id: task?.id,
        type: task?.type,
        description: task?.description,
        dimensionItems: task?.dimensionItems,
        dimensions,
        assigneeName: task?.assigneeName,
        assigneeId: task?.assigneeId,
        inviteeEmail: task?.inviteeEmail,
        year: task?.period?.year?.toString() ?? "",
        quarter: task?.period?.quarter ?? "",
        status: t("status." + task?.status),
        questionnaires: getRequestedQuestionnaires(task.configurations),
        configurations: task?.configurations,
        period: task?.period,
        originalStatus: task?.status,
        updatable: task?.updatable,
        lastReminder: task?.lastReminder
      };
    });
    setTasksList(tasks);
    if (pageContent.length === 0 && data?.number !== 0 && data?.totalElements !== 0 && queryPageIndex > 0) {
      manageDataTableState({
        totalElements: data?.totalElements ?? 0,
        totalPages: data?.totalPages ?? 0,
        queryPageIndex: queryPageIndex - 1
      });
    } else {
      manageDataTableState({
        totalElements: data?.totalElements ?? 0,
        totalPages: data?.totalPages ?? 0
      });
    }
  }, [organizationStructure, t, queryPageIndex]);

  const queryConfig = {
    onSuccess: handleTasksSuccess,
    onError: err => handleErrors(err),
    cacheTime: 0,
    onSettled: () => dispatch(hideAppSpinner()),
  };

  const {
    isLoading: isLoadingAdminTasks,
    isFetching: isFetchingAdminTasks,
    refetch: refetchAdminTasks
  } = useGetAllDataEntryPlanningQuestionnaires(constructQueryParams(), {
    ...queryConfig,
    enabled: isAdmin,
  });

  const {
    isLoading: isLoadingMemberTasks,
    isFetching: isFetchingMemberTasks,
    refetch: refetchMemberTasks
  } = useGetOrganisationQuestionnaires(
    {
      page: 0,
      size: 9999,
      sort: "id,desc",
    },
    {
      ...queryConfig,
      enabled: !isAdmin,
    }
  );

  const isLoadingTasks = isAdmin ? isLoadingAdminTasks : isLoadingMemberTasks;
  const isFetchingTasks = isAdmin ? isFetchingAdminTasks : isFetchingMemberTasks;
  const refetchTasks = isAdmin ? refetchAdminTasks : refetchMemberTasks;
  refetchTasksRef.current = refetchTasks;


  useEffect(() => {
    refetchTasks();
  }, [refetchTasks, viewType]);

  const closeReviewQuestionnaireModal = () => {
    setSelectedQuestionnaireId("");
    onReviewQuestionnaireClose();
  };

  const handleLocalFilterChange = useCallback((attribute, value) => {
    setLocalFilters((prev) => ({ ...prev, [attribute]: value }));
  }, []);

  const dimItemKeyMap = useMemo(() => ({
    dimensionOne: "dimensionOneItem",
    dimensionTwo: "dimensionTwoItem",
    dimensionThree: "dimensionThreeItem",
    dimensionFour: "dimensionFourItem",
    dimensionFive: "dimensionFiveItem",
  }), []);

  const applyFiltersExcluding = useCallback((list, excludeKey) => {
    if (searchTerm && excludeKey !== "search") {
      const term = searchTerm.toLowerCase();
      list = list.filter((t) => {
        // Dimension item names (e.g. "France", "Europe") live in the
        // mapped dimensions array as { name, value } pairs. Skip "All"
        // values — they aren't real selections and would match every
        // task the moment a user types "all".
        const dimensionMatch = (t.dimensions || []).some((d) =>
          d.value && d.value !== "All" && d.value.toLowerCase().includes(term)
        );
        return (
          (t.description || "").toLowerCase().includes(term) ||
          (t.assigneeName || "").toLowerCase().includes(term) ||
          (t.questionnaires || "").toLowerCase().includes(term) ||
          (t.year || "").toLowerCase().includes(term) ||
          (t.quarter || "").toLowerCase().includes(term) ||
          dimensionMatch
        );
      });
    }
    if (excludeKey !== "status" && filterObj.status?.length > 0) {
      const vals = filterObj.status.map((f) => f.value);
      list = list.filter((t) => vals.includes(t.originalStatus));
    }
    if (excludeKey !== "quarter" && filterObj.quarter?.length > 0) {
      const vals = filterObj.quarter.map((f) => f.value);
      list = list.filter((t) => vals.includes(t.quarter));
    }
    if (excludeKey !== "year" && localFilters.year?.length > 0) {
      const vals = localFilters.year.map((f) => f.value);
      list = list.filter((t) => vals.includes(t.year));
    }
    if (excludeKey !== "assignee" && localFilters.assignee?.length > 0) {
      const vals = localFilters.assignee.map((f) => f.value);
      list = list.filter((t) => vals.includes(t.assigneeName));
    }
    if (excludeKey !== "scope" && localFilters.scope?.length > 0) {
      const vals = localFilters.scope.map((f) => f.value);
      list = list.filter((t) => {
        if (!t.questionnaires) return false;
        const taskScopes = t.questionnaires.split(", ").map((s) => s.trim());
        return vals.some((v) => taskScopes.includes(v));
      });
    }
    if (excludeKey !== "dimension" && localFilters.dimension?.length > 0) {
      list = list.filter((t) => {
        if (!t.dimensionItems) return false;
        return localFilters.dimension.some((f) =>
          t.dimensionItems[dimItemKeyMap[f.dimKey]] === f.itemId
        );
      });
    }
    return list;
  }, [filterObj, localFilters, searchTerm, dimItemKeyMap]);

  const filteredTasksList = useMemo(() => {
    return applyFiltersExcluding([...tasksList], null);
  }, [tasksList, applyFiltersExcluding]);

  // Grid view default sort: year desc → quarter (QUARTERS enum order) →
  // status (No data → In Progress → Finalised). List view keeps its
  // DataTable-driven sort and is rendered straight from filteredTasksList.
  const gridTasksList = useMemo(() => {
    const statusOrder = {
      [QUESTIONNAIRE_STATUSES.NEW]: 0,
      [QUESTIONNAIRE_STATUSES.IN_PROGRESS]: 1,
      [QUESTIONNAIRE_STATUSES.FINALIZED]: 2,
    };
    const quarterOrder = Object.keys(QUARTERS).reduce((acc, key, idx) => {
      acc[key] = idx;
      return acc;
    }, {});
    return [...filteredTasksList].sort((a, b) => {
      const ya = parseInt(a.year, 10);
      const yb = parseInt(b.year, 10);
      const yearA = Number.isFinite(ya) ? ya : -Infinity;
      const yearB = Number.isFinite(yb) ? yb : -Infinity;
      if (yearA !== yearB) return yearB - yearA;
      const qa = quarterOrder[a.quarter] ?? 99;
      const qb = quarterOrder[b.quarter] ?? 99;
      if (qa !== qb) return qa - qb;
      const sa = statusOrder[a.originalStatus] ?? 99;
      const sb = statusOrder[b.originalStatus] ?? 99;
      return sa - sb;
    });
  }, [filteredTasksList]);

  // Keep the ref in sync with the filtered list so the columns-memo header
  // (which is built above filteredTasksList) can read the latest list.
  // Also drop any selections that no longer match the current filter set.
  useEffect(() => {
    filteredTasksListRef.current = filteredTasksList;
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visibleIds = new Set(filteredTasksList.map((task) => task.id));
      let changed = false;
      const next = new Set();
      prev.forEach((id) => {
        if (visibleIds.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [filteredTasksList]);

  const selectedTasks = useMemo(
    () => filteredTasksList.filter((task) => selectedIds.has(task.id)),
    [filteredTasksList, selectedIds]
  );

  const selectionStatus = useMemo(() => {
    const statuses = new Set(selectedTasks.map((t) => t.originalStatus));
    return {
      allFinalised: selectedTasks.length > 0 && statuses.size === 1 && statuses.has(QUESTIONNAIRE_STATUSES.FINALIZED),
      noneFinalised: selectedTasks.length > 0 && !statuses.has(QUESTIONNAIRE_STATUSES.FINALIZED),
    };
  }, [selectedTasks]);
  // Finalised tasks cannot be deleted — they must be unfinalised first.
  const canBulkDelete = selectionStatus.noneFinalised;

  const yearOptions = useMemo(() => {
    const list = applyFiltersExcluding([...tasksList], "year");
    const unique = [...new Set(list.map((t) => t.year).filter(Boolean))];
    return unique.sort().reverse().map((year) => ({ label: year, value: year }));
  }, [tasksList, applyFiltersExcluding]);

  const quarterOptions = useMemo(() => {
    const list = applyFiltersExcluding([...tasksList], "quarter");
    const unique = [...new Set(list.map((t) => t.quarter).filter(Boolean))];
    const quarterOrder = Object.keys(QUARTERS);
    return unique
      .sort((a, b) => quarterOrder.indexOf(a) - quarterOrder.indexOf(b))
      .map((q) => ({ label: q, value: q }));
  }, [tasksList, applyFiltersExcluding]);

  const statusOptions = useMemo(() => {
    const list = applyFiltersExcluding([...tasksList], "status");
    const unique = [...new Set(list.map((t) => t.originalStatus).filter(Boolean))];
    const statusOrder = [QUESTIONNAIRE_STATUSES.NEW, QUESTIONNAIRE_STATUSES.IN_PROGRESS, QUESTIONNAIRE_STATUSES.FINALIZED];
    return unique
      .sort((a, b) => statusOrder.indexOf(a) - statusOrder.indexOf(b))
      .map((status) => ({ label: t(`status.${status}`), value: status }));
  }, [tasksList, applyFiltersExcluding, t]);

  const assigneeOptions = useMemo(() => {
    const list = applyFiltersExcluding([...tasksList], "assignee");
    const unique = [...new Set(list.map((t) => t.assigneeName).filter(Boolean))];
    return unique.sort(compareLabels).map((name) => ({ label: name, value: name }));
  }, [tasksList, applyFiltersExcluding]);

  const scopeOptions = useMemo(() => {
    const list = applyFiltersExcluding([...tasksList], "scope");
    const allScopes = new Set();
    list.forEach((t) => {
      if (t.questionnaires) {
        t.questionnaires.split(", ").forEach((scope) => allScopes.add(scope.trim()));
      }
    });
    return [...allScopes].sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .map((scope) => ({ label: scope, value: scope }));
  }, [tasksList, applyFiltersExcluding]);

  const dimensionGroups = useMemo(() => {
    if (!organizationStructure?.dimensions) return [];
    const list = applyFiltersExcluding([...tasksList], "dimension");
    const activeDimItems = new Set();
    list.forEach((t) => {
      if (!t.dimensionItems) return;
      dimensionsKeys.forEach((dimKey) => {
        const itemId = t.dimensionItems[dimItemKeyMap[dimKey]];
        if (itemId) activeDimItems.add(`${dimKey}:${itemId}`);
      });
    });
    return dimensionsKeys
      .filter((dimKey) => {
        const dim = organizationStructure.dimensions[dimKey];
        return dim && dim.items?.length > 0;
      })
      .map((dimKey) => ({
        dimKey,
        name: organizationStructure.dimensions[dimKey].name,
        items: organizationStructure.dimensions[dimKey].items.filter(
          (item) => activeDimItems.has(`${dimKey}:${item.id}`)
        ),
      }))
      .filter((group) => group.items.length > 0);
  }, [organizationStructure, tasksList, applyFiltersExcluding, dimItemKeyMap]);

  return <Stack spacing={10} p={4} bg="pureWhite" mx="15%">
    <Box>
      <Heading fontSize="48px" fontWeight="700" color="darkTeal">
        {t("page.title")}
      </Heading>
      <Text fontSize="16px" color="gray.500" maxW="75%">
        {t("page.subTitle")}
      </Text>
    </Box>

    <TaskFilterBar
      filterObj={filterObj}
      onServerFilterChange={(attribute, value) => manageDataTableState({
        filterObj: { attribute, value },
        queryPageIndex: 0
      })}
      localFilters={localFilters}
      onLocalFilterChange={handleLocalFilterChange}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      statusOptions={statusOptions}
      quarterOptions={quarterOptions}
      yearOptions={yearOptions}
      assigneeOptions={assigneeOptions}
      scopeOptions={scopeOptions}
      dimensionGroups={dimensionGroups}
      showAssigneeFilter={isAdmin}
    />

    <Flex justify="space-between" align="flex-start">
      <Flex direction="column" align="flex-start" gap={3}>
        <Text fontSize="18px" fontWeight="700" color="gray.600">
          {filteredTasksList.length} {filteredTasksList.length === 1 ? "task" : "tasks"}
          {filteredTasksList.length !== tasksList.length && ` of ${totalElements}`}
        </Text>
        {isAdmin && viewType === "list" && (
          <Flex gap={2}>
            <Button
              bg="limeShade.600"
              color="white"
              borderRadius="full"
              _hover={{ bg: "limeShade.700" }}
              size="sm"
              onClick={onCreateNew}
            >
              Assign new task
            </Button>
            <Button
              bg="limeShade.600"
              color="white"
              borderRadius="full"
              _hover={{ bg: "limeShade.700" }}
              size="sm"
              onClick={() => navigate(routes.dataCollectionAddData)}
            >
              Add data
            </Button>
          </Flex>
        )}
      </Flex>
      <Flex direction="column" align="flex-end" gap={1}>
      <Flex
        bg="gray.100"
        borderRadius="full"
        p="3px"
      >
        <Button
          size="sm"
          borderRadius="full"
          bg={viewType === "list" ? "darkTeal" : "transparent"}
          color={viewType === "list" ? "white" : "gray.500"}
          _hover={{ bg: viewType === "list" ? "darkTeal" : "gray.300" }}
          rightIcon={<Rows size={16} />}
          fontWeight="600"
          fontSize="13px"
          px={4}
          onClick={() => handleSetViewType("list")}
        >
          List
        </Button>
        <Button
          size="sm"
          borderRadius="full"
          bg={viewType === "grid" ? "darkTeal" : "transparent"}
          color={viewType === "grid" ? "white" : "gray.500"}
          _hover={{ bg: viewType === "grid" ? "darkTeal" : "gray.300" }}
          rightIcon={<SquaresFour size={16} />}
          fontWeight="600"
          fontSize="13px"
          px={4}
          onClick={() => handleSetViewType("grid")}
        >
          Grid
        </Button>
      </Flex>
      </Flex>
    </Flex>

    <DeleteDialog
      isOpen={isConfirmDialogOpen}
      onClose={onConfirmDialogClose}
      onDelete={onDelete}
      entityName={"Task"}
    />

    <ConfirmationDialog
      isOpen={isBulkDialogOpen}
      onClose={() => { onBulkDialogClose(); setBulkAction(null); }}
      onConfirm={confirmBulkDialog}
      headerText={
        bulkAction === "delete"
          ? `Delete ${selectedIds.size} task${selectedIds.size !== 1 ? "s" : ""}?`
          : bulkAction === "finalise"
            ? `Finalise ${selectedIds.size} task${selectedIds.size !== 1 ? "s" : ""}?`
            : `Unfinalise ${selectedIds.size} task${selectedIds.size !== 1 ? "s" : ""}?`
      }
      confirmationText={
        <Box>
          <Text mb={2}>
            {bulkAction === "delete"
              ? "This cannot be undone."
              : bulkAction === "finalise"
                ? "Finalised tasks count towards emissions calculations and are locked from editing."
                : "Unfinalised tasks are removed from emissions calculations until re-finalised."}
          </Text>
          {selectedTasks.slice(0, 5).map((task) => (
            <Text key={task.id} fontSize="13px" color="gray.600" noOfLines={1}>• {task.description || `Task #${task.id}`}</Text>
          ))}
          {selectedTasks.length > 5 && (
            <Text fontSize="13px" color="gray.500" fontStyle="italic">… and {selectedTasks.length - 5} more</Text>
          )}
        </Box>
      }
    />

    <ReviewQuestionnaireModal
      questionnaireId={selectedQuestionnaireId}
      isOpen={isReviewQuestionnaireOpen}
      onClose={closeReviewQuestionnaireModal}
    />

    {isAdmin && viewType === "grid" && !selectionMode && (
      <Flex justify="flex-end" mt={-4} mb={-8}>
        <HStack
          as="button"
          spacing={1.5}
          fontSize="13px"
          fontWeight="500"
          color="gray.500"
          _hover={{ color: "gray.700" }}
          onClick={toggleSelectionMode}
        >
          <Checks size={16} weight="bold" />
          <Text>Select multiple</Text>
        </HStack>
      </Flex>
    )}

    {isAdmin && (selectionMode || selectedIds.size > 0) && (
      <Flex
        bg="darkTeal"
        color="white"
        borderRadius="lg"
        px={4}
        py={3}
        align="center"
        justify="space-between"
      >
        <HStack spacing={4}>
          <Text fontSize="14px" fontWeight="600">
            {selectedIds.size} selected
          </Text>
          {selectedIds.size > 0 && (
            <Button
              size="sm"
              variant="ghost"
              color="white"
              fontSize="13px"
              _hover={{ bg: "whiteAlpha.200" }}
              onClick={clearSelection}
            >
              Clear
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            color="white"
            fontSize="13px"
            _hover={{ bg: "whiteAlpha.200" }}
            onClick={toggleSelectionMode}
          >
            Cancel
          </Button>
        </HStack>
        <HStack spacing={2}>
          <Tooltip label={selectedIds.size === 0 ? "Select tasks first" : !selectionStatus.noneFinalised ? "Finalise is only available when all selected tasks are not yet finalised" : ""}>
            <Button
              size="sm"
              borderRadius="full"
              leftIcon={<CheckCircle size={14} weight="fill" />}
              bg="whiteAlpha.200"
              color="white"
              _hover={{ bg: "whiteAlpha.300" }}
              isDisabled={selectedIds.size === 0 || !selectionStatus.noneFinalised}
              onClick={() => openBulkDialog("finalise")}
            >
              Finalise
            </Button>
          </Tooltip>
          <Tooltip label={selectedIds.size === 0 ? "Select tasks first" : !selectionStatus.allFinalised ? "Unfinalise is only available when all selected tasks are finalised" : ""}>
            <Button
              size="sm"
              borderRadius="full"
              leftIcon={<ArrowCounterClockwise size={14} weight="bold" />}
              bg="whiteAlpha.200"
              color="white"
              _hover={{ bg: "whiteAlpha.300" }}
              isDisabled={selectedIds.size === 0 || !selectionStatus.allFinalised}
              onClick={() => openBulkDialog("unfinalise")}
            >
              Unfinalise
            </Button>
          </Tooltip>
          <Tooltip label={selectedIds.size === 0 ? "Select tasks first" : !canBulkDelete ? "Finalised tasks cannot be deleted — unfinalise them first" : ""}>
            <Button
              size="sm"
              borderRadius="full"
              leftIcon={<Trash size={14} weight="bold" />}
              bg="red.500"
              color="white"
              _hover={{ bg: "red.600" }}
              isDisabled={selectedIds.size === 0 || !canBulkDelete}
              onClick={() => openBulkDialog("delete")}
            >
              Delete
            </Button>
          </Tooltip>
        </HStack>
      </Flex>
    )}

    {viewType === "list" ? (
      <Stack spacing={3}>
        <DataTable
          data={filteredTasksList}
          columns={columns}
          isLoading={isLoadingTasks || isFetchingTasks}
          enableFilters={false}
          enableSorting={true}
          containerHeight="var(--chakra-sizes-2xl)"
          showDownloadButton={false}
        />
      </Stack>
    ) : (
      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
        {isAdmin && (
          <Box
            w="100%"
            border="1px solid"
            borderColor="lime"
            borderRadius="lg"
            bg="lightLime"
            _hover={{ borderColor: "limeShade.400" }}
            transition="all 0.15s"
            overflow="hidden"
            position="relative"
            px={6}
            pt={5}
            pb={8}
          >
            <chakra.svg
              position="absolute"
              right="0"
              bottom="0"
              width="220px"
              height="180px"
              viewBox="0 0 175 114"
              preserveAspectRatio="xMaxYMax meet"
              fill="none"
              pointerEvents="none"
            >
              <path d="M31.2441 113.272C31.2066 113.31 31.1694 113.349 31.1318 113.387H0C4.3128 108.084 9.24419 102.6 14.8643 96.9404L86.3467 25.458L64.8271 25.4736L64.7959 2.35255H142.156L31.2441 113.272Z" fill="#85C22A" fillOpacity="0.15" />
              <path d="M174.876 18.7002L80.2031 113.387H47.501L158.521 2.36036H174.876V18.7002Z" fill="#85C22A" fillOpacity="0.15" />
              <path d="M174.884 112.425H174.999V113.387H174.87C174.873 113.069 174.876 112.751 174.876 112.433L151.763 112.362V90.6221L128.998 113.387H96.2949L174.884 34.7969V112.425Z" fill="#85C22A" fillOpacity="0.15" />
            </chakra.svg>
            <Text fontSize="22px" fontWeight="700" color="darkTeal" mb={3}>
              Add Data
            </Text>
            <Text fontSize="14px" color="gray.600" mb={5} maxW="70%" position="relative" zIndex={1}>
              Add data or assign a new data collection tasks to track, manage and calculate emissions.
            </Text>
            <Flex gap={2} position="relative" zIndex={1}>
              <Button
                bg="limeShade.600"
                color="white"
                borderRadius="full"
                _hover={{ bg: "limeShade.700" }}
                size="sm"
                onClick={onCreateNew}
              >
                Assign new task
              </Button>
              <Button
                bg="limeShade.600"
                color="white"
                borderRadius="full"
                _hover={{ bg: "limeShade.700" }}
                size="sm"
                onClick={() => navigate(routes.dataCollectionAddData)}
              >
                Add data
              </Button>
            </Flex>
          </Box>
        )}
        {gridTasksList.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            isJustReminded={justRemindedIds.has(task.id)}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onView={handleViewQuestionnaire}
            onEditData={handleEditQuestionnaire}
            onUnfinalise={handleUnfinaliseQuestionnaire}
            onSendReminder={openSendReminder}
            t={t}
            isAdmin={isAdmin}
            isSelected={selectedIds.has(task.id)}
            onToggleSelect={isAdmin ? toggleSelected : undefined}
            selectionMode={selectionMode}
          />
        ))}
      </SimpleGrid>
    )}
    <SendReminderModal
      isOpen={isReminderOpen}
      task={reminderTask}
      onClose={onReminderClose}
      onSent={handleReminderSent}
    />
  </Stack>;
}

export default WithAuthorization(OrganizationPlanning);